/*!
 * A11Y Navigation v0.2.1
 *
 * Copyright (c) 2019 Fynn Becker
 * This source code is licensed under the MIT license.
 */
function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}

function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  return Constructor;
}

var mq = window.matchMedia('(hover)');
/**
 * Try to detect hover capability of device
 * @param {Function} [callback] Add callback to media query listener
 * @returns {Boolean} Hover capability
 */

function detectHover (callback) {
  if (typeof callback === 'function') {
    mq.addListener(function (event) {
      return callback(event.matches);
    });
  }

  return mq.matches;
}

/**
 * Detect touch enabled device
 * @param {Function} callback Called with TouchEvent when touch starts
 */
function detectTouch (callback) {
  window.addEventListener('touchstart', function handler(event) {
    callback(event);
    window.removeEventListener('touchstart', handler);
  });
}

/**
 * Item class
 */

var Item =
/*#__PURE__*/
function () {
  /**
   * Create new Item
   * @param {Object} options Configuration
   * @param {HTMLElement} options.el Item element
   * @param {string} options.id Item ID
   * @param {Item[]} options.items Items of current A11yNavigation
   * @param {string} options.selectorButton Button selector
   * @param {string} options.selectorChild Child selector
   * @param {string} options.selectorLink Link selector
   * @param {string} options.classOpen Toggle this class
   */
  function Item(options) {
    var _this = this;

    _classCallCheck(this, Item);

    this.el = options.el;
    this.id = options.id;
    this.items = options.items;
    this.button = options.el.querySelector(options.selectorButton);
    this.child = options.el.querySelector(options.selectorChild);
    this.link = options.el.querySelector(options.selectorLink);
    this.config = {
      classOpen: options.classOpen,
      childId: "".concat(this.id, "-child")
    };
    this.isOpen = this.el.classList.contains(this.config.classOpen) || false;
    this.canHover = detectHover();
    this.canTouch = false;
    this.setButtonVisibility();
    this.setAttributes();
    this.button.setAttribute('aria-controls', this.config.childId);
    this.link.setAttribute('aria-controls', this.config.childId);
    this.child.id = this.config.childId;
    this.button.addEventListener('click', function () {
      return _this.toggle();
    });
    this.link.addEventListener('focus', function () {
      return _this.open();
    });
    this.el.addEventListener('mouseenter', function () {
      // Open if user isn’t touching
      if (!_this.canTouch) {
        _this.open();
      }
    });
    this.el.addEventListener('mouseleave', function () {
      // Close if user isn’t touching
      if (!_this.canTouch) {
        _this.close();
      }
    });
    this.link.addEventListener('keydown', function (event) {
      // Close if user tabs back
      if (event.shiftKey && event.keyCode === 9) {
        _this.close();
      }
    });
    var childLinks = this.child.querySelectorAll('a');
    childLinks[childLinks.length - 1].addEventListener('keydown', function (event) {
      // Close if user tabs out
      if (!event.shiftKey && event.keyCode === 9) {
        _this.close();
      }
    });
    detectHover(function (canHover) {
      _this.canHover = canHover;

      _this.setButtonVisibility();
    });
    detectTouch(function () {
      _this.canTouch = true;
    });
  }
  /**
   * Set button visibility state
   */


  _createClass(Item, [{
    key: "setButtonVisibility",
    value: function setButtonVisibility() {
      if (this.canHover) {
        this.button.setAttribute('hidden', 'hidden');
      } else {
        this.button.removeAttribute('hidden');
      }
    }
    /**
     * Set element attributes
     */

  }, {
    key: "setAttributes",
    value: function setAttributes() {
      if (this.isOpen) {
        this.button.setAttribute('aria-expanded', 'true');
        this.child.setAttribute('aria-hidden', 'false');
        this.link.setAttribute('aria-expanded', 'true');
      } else {
        this.button.setAttribute('aria-expanded', 'false');
        this.child.setAttribute('aria-hidden', 'true');
        this.link.setAttribute('aria-expanded', 'false');
      }
    }
    /**
     * Add open class and set attributes
     */

  }, {
    key: "open",
    value: function open() {
      var _this2 = this;

      this.isOpen = true; // Close all other items before opening the current

      this.items.forEach(function (item) {
        return item !== _this2 && item.close();
      });
      this.el.classList.add(this.config.classOpen);
      this.setAttributes();
    }
    /**
     * Remove open class and set attributes
     */

  }, {
    key: "close",
    value: function close() {
      this.isOpen = false;
      this.el.classList.remove(this.config.classOpen);
      this.setAttributes();
    }
    /**
     * Toggle between open and close state
     */

  }, {
    key: "toggle",
    value: function toggle() {
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    }
  }]);

  return Item;
}();

/**
 * A11yNavigation class
 */

var A11yNavigation =
/**
 * Create new A11yNavigation
 * @param {Object} options Configuration
 * @param {HTMLElement} options.el Container element
 * @param {string} [options.id] Navigation ID
 * @param {string} [options.classOpen=is-open] Toggle this class
 * @param {string} [options.selectorButton=.js-nav-button] Button selector
 * @param {string} [options.selectorChild=.js-nav-child] Child selector
 * @param {string} [options.selectorItem=.js-nav-item] Item selector (contains button, child, and link elements)
 * @param {string} [options.selectorLink=.js-nav-link] Link selector
 */
function A11yNavigation(options) {
  _classCallCheck(this, A11yNavigation);

  this.el = options.el;
  this.id = options.id || this.el.id;
  this.items = [];
  this.config = {
    classOpen: options.classOpen || 'is-open',
    selectorButton: options.selectorButton || '.js-nav-button',
    selectorChild: options.selectorChild || '.js-nav-child',
    selectorItem: options.selectorItem || '.js-nav-item',
    selectorLink: options.selectorLink || '.js-nav-link'
  };
  var items = this.el.querySelectorAll(this.config.selectorItem);

  for (var i = 0; i < items.length; i++) {
    this.items.push(new Item({
      el: items[i],
      id: "".concat(this.id, "-item-").concat(i + 1),
      items: this.items,
      classOpen: this.config.classOpen,
      selectorButton: this.config.selectorButton,
      selectorChild: this.config.selectorChild,
      selectorLink: this.config.selectorLink
    }));
  }
};

export default A11yNavigation;



//////////////////
// WEBPACK FOOTER
// ./node_modules/a11y-navigation/dist/a11y-navigation.esm.js
// module id = 7
// module chunks = 1